import { useEffect, useRef } from "react";
import {
  animate,
  motion,
  useInView,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "motion/react";

/*
 * Migrada's motion language for the public site.
 *
 * Energetic where it rewards attention (headlines, numbers, a card you touch),
 * calm everywhere else. Three rules keep it from feeling like an advert:
 *   • motion always reveals something real: a word, a figure, a step
 *   • springs settle quickly and never wobble more than once
 *   • everything works without motion. <MotionConfig reducedMotion="user"> in
 *     App.tsx turns transforms off for people who ask their OS for less, and
 *     final values are always rendered first, so pre-rendered pages and search
 *     engines see real text and real numbers.
 */

export const EASE_OUT = [0.22, 0.61, 0.36, 1] as const;
/** A fast start that lands softly. Used for reveals that should feel snappy. */
export const EASE_EXPO = [0.16, 1, 0.3, 1] as const;
/** One small overshoot, then still. */
export const SPRING_POP = { type: "spring", stiffness: 420, damping: 24, mass: 0.7 } as const;
export const SPRING_SOFT = { type: "spring", stiffness: 220, damping: 24 } as const;

type HeadingTag = "h1" | "h2" | "h3" | "p" | "span";

/**
 * Words rise out of a mask, one after another, when the heading scrolls into
 * view. The words stay plain text separated by real spaces, so the heading
 * reads, wraps, balances and indexes exactly like an unsplit one.
 */
export function KineticHeading({
  text,
  as = "h2",
  className = "",
  style,
  delay = 0,
  stagger = 0.055,
  onMount = false,
}: {
  text: string;
  as?: HeadingTag;
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
  stagger?: number;
  /** Animate on first render instead of on scroll (for content above the fold). */
  onMount?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const play = onMount || inView;
  const Tag = as as React.ElementType;
  const words = text.split(" ");

  return (
    <Tag ref={ref} className={className} style={style}>
      {words.map((word, i) => (
        <span key={`${word}-${i}`}>
          {/* The mask. Padding keeps descenders (g, y, p) from being clipped. */}
          <span className="inline-block overflow-hidden pb-[0.14em] -mb-[0.14em] align-bottom">
            <motion.span
              className="inline-block will-change-transform"
              initial={{ y: "105%", rotate: 4 }}
              animate={play ? { y: "0%", rotate: 0 } : undefined}
              transition={{ duration: 0.8, delay: delay + i * stagger, ease: EASE_EXPO }}
            >
              {word}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : null}
        </span>
      ))}
    </Tag>
  );
}

/**
 * A number that counts up once, the first time it is seen. The final value is
 * what renders first, so it is never wrong for a crawler, a screenshot or a
 * reader who has motion turned off.
 */
export function CountUp({
  value,
  format,
  duration = 1.4,
  delay = 0,
  className,
  style,
}: {
  value: number;
  format: (n: number) => string;
  duration?: number;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!inView || !el || reduce || value === 0) return;
    const whole = Number.isInteger(value);
    const controls = animate(0, value, {
      duration,
      delay,
      ease: EASE_EXPO,
      onUpdate: (v) => {
        el.textContent = format(whole ? Math.round(v) : Math.round(v * 100) / 100);
      },
      onComplete: () => {
        el.textContent = format(value);
      },
    });
    return () => controls.stop();
  }, [inView, reduce, value, format, duration, delay]);

  return (
    <span ref={ref} className={className} style={style}>
      {format(value)}
    </span>
  );
}

/**
 * Tilts towards the pointer like a card in your hand, with a soft light that
 * follows it. Mouse only: on touch screens it stays flat, so scrolling never
 * fights with it.
 */
export function TiltCard({
  children,
  className = "",
  style,
  max = 7,
  glare = true,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  max?: number;
  glare?: boolean;
}) {
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(py, [0, 1], [max, -max]), { stiffness: 180, damping: 18 });
  const rotateY = useSpring(useTransform(px, [0, 1], [-max, max]), { stiffness: 180, damping: 18 });
  const gx = useTransform(px, (v) => `${v * 100}%`);
  const gy = useTransform(py, (v) => `${v * 100}%`);
  const light = useMotionTemplate`radial-gradient(420px circle at ${gx} ${gy}, rgba(255,255,255,0.55), transparent 55%)`;
  const lightOpacity = useSpring(0, { stiffness: 200, damping: 30 });

  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
    lightOpacity.set(1);
  };
  const onLeave = () => {
    px.set(0.5);
    py.set(0.5);
    lightOpacity.set(0);
  };

  return (
    <motion.div
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      className={`relative [transform-style:preserve-3d] ${className}`}
      style={{ ...style, rotateX, rotateY, transformPerspective: 1000 }}
    >
      {children}
      {glare && (
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 mix-blend-soft-light"
          style={{ background: light, opacity: lightOpacity, borderRadius: "inherit" }}
        />
      )}
    </motion.div>
  );
}

/**
 * Leans gently towards the cursor, then springs back. For the one or two
 * buttons on a screen that matter most.
 */
export function Magnetic({ children, strength = 0.22, className = "" }: { children: React.ReactNode; strength?: number; className?: string }) {
  const x = useSpring(0, SPRING_SOFT);
  const y = useSpring(0, SPRING_SOFT);

  const onMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.span onPointerMove={onMove} onPointerLeave={reset} className={`inline-block ${className}`} style={{ x, y }}>
      {children}
    </motion.span>
  );
}

const BURST_COLOURS = ["#2EE6D6", "#00786F", "#C6872F", "#0A0F0E", "#CFF9F4"];

/**
 * A small celebration: a ring of shapes that flies out once and fades. Sits
 * behind whatever it wraps, for moments that deserve it (a request sent).
 */
export function Burst({ pieces = 14, radius = 86 }: { pieces?: number; radius?: number }) {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-0 w-0">
      {Array.from({ length: pieces }, (_, i) => {
        const angle = (i / pieces) * Math.PI * 2 + (i % 2 ? 0.2 : 0);
        const dist = radius * (i % 3 === 0 ? 1.15 : i % 3 === 1 ? 0.85 : 1);
        const size = i % 3 === 0 ? 10 : 7;
        return (
          <motion.span
            key={i}
            className="absolute block"
            style={{
              width: size,
              height: i % 4 === 0 ? size * 2.2 : size,
              marginLeft: -size / 2,
              marginTop: -size / 2,
              backgroundColor: BURST_COLOURS[i % BURST_COLOURS.length],
              borderRadius: i % 2 ? 999 : 3,
            }}
            initial={{ x: 0, y: 0, scale: 0.2, opacity: 1, rotate: 0 }}
            animate={{ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, scale: 1, opacity: 0, rotate: i % 2 ? 160 : -120 }}
            transition={{ duration: 1.1, ease: EASE_EXPO, opacity: { duration: 1.1, ease: "easeIn" } }}
          />
        );
      })}
    </span>
  );
}

/** Fades and lifts a block in once, as it scrolls into view. */
export function Reveal({
  children,
  delay = 0,
  className = "",
  y = 18,
  as = "div",
  style,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  y?: number;
  as?: "div" | "li" | "article" | "section";
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const Tag = motion[as] as typeof motion.div;
  return (
    <Tag
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.75, delay, ease: EASE_EXPO }}
      className={className}
      style={style}
    >
      {children}
    </Tag>
  );
}
